/* eslint-disable no-underscore-dangle */
import React, {
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  Droppable,
  DragDropContext,
} from 'react-beautiful-dnd';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  updateItemPositonOnList,
} from 'web-utils';

import {
  getUiState,
} from 'web-selectors';
import {
  uiActions,
} from 'web-actions';

import Week from './Week';
import * as S from './styled';

type Props = {
  readOnly: boolean,
  programId: number,
  resourceType: string,
};

const ProgramWeeks = ({ readOnly, programId, resourceType }: Props) => {
  const isPersonal = resourceType === 'personalPrograms';
  const programType = isPersonal
    ? 'PersonalProgram'
    : 'ProgramTemplate';

  const mapState = useCallback((state) => {
    const program = coreSelectors.getResourceById(
      resourceType,
      programId,
    )(state);

    return {
      weeks: coreSelectors.getProgamWeeks(
        programId,
        resourceType,
      )(state),
      workoutOptions: coreSelectors.getProgamWorkoutOptions(
        isPersonal ? program.person_id : 'me',
      )(state),
      programWorkouts: coreSelectors.getResourceMappedList(
        'programWorkouts',
        `program_${programId}`,
      )(state),
      selectedProgramWorkouts: getUiState('selectedProgramWorkouts')(state),
    };
  }, [programId]);

  const {
    weeks,
    workoutOptions,
    programWorkouts,
    selectedProgramWorkouts,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const selectedWorkout = selectedProgramWorkouts[programId];

  const handleSelect = (id, weekId) => {
    dispatch(
      uiActions.setUiState({
        selectedProgramWorkouts: {
          _merge: true,
          [programId]: {
            id,
            weekId,
          },
        },
      }),
    );
  };

  const makeFirstSelect = () => {
    for (let i = 0; i < weeks.length; i += 1) {
      const { workouts } = weeks[i];
      if (workouts.length) {
        handleSelect(
          workouts[0].id,
          workouts[0].week_id,
        );
        break;
      }
    }
  };

  // make selected the first workout
  useEffect(() => {
    if (!selectedWorkout) {
      makeFirstSelect();
    }
  }, [programId]);

  useEffect(() => {
    if (
      programWorkouts.length
      && !programWorkouts.some(w => w.id === selectedWorkout?.id)) {
      makeFirstSelect();
    }
  }, [programWorkouts.length]);

  const handleSelectWorkout = (workout) => {
    if (workout.id !== selectedWorkout?.id) {
      handleSelect(
        workout.id,
        workout.week_id,
      );
    }
  };

  const handleDragEnd = (result) => {
    const {
      type,
      source,
      destination,
    } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }

    const toWeekId = +destination.droppableId;
    const fromWeekId = +source.droppableId;
    const draggableId = +result.draggableId;

    if (type === 'workout') {
      // same position
      if (source.index === destination.index && fromWeekId === toWeekId) {
        return;
      }
      const fromList = Array.from(
        weeks.find(w => w.id === fromWeekId)
          .workouts
          .filter(w => w.id !== draggableId),
      );
      let toList: any[] = [];

      const target = {
        id: draggableId,
        week_id: (toWeekId !== -1) ? toWeekId : null,
      };

      if (fromWeekId !== toWeekId && toWeekId !== -1) {
        toList = Array.from(
          weeks.find(w => w.id === toWeekId).workouts,
        );
        toList.splice(destination.index, 0, target);
      } else {
        fromList.splice(destination.index, 0, target);
      }

      const resources = [
        ...updateItemPositonOnList(fromList),
        ...updateItemPositonOnList(toList),
      ];

      dispatch(
        coreActions.updateResources({
          resourceType: 'programWorkouts',
          resources,
        }),
      );

      dispatch(
        coreActions.requestUpdateItems({
          resources,
          resourceType: 'programWorkouts',
        }),
      );
    } else if (type === 'week') {
      // same position
      if (source.index === destination.index) {
        return;
      }

      const newList = Array.from(
        weeks.filter((w, i) => w.id !== -1 && i !== source.index),
      );

      const target = {
        id: draggableId,
      };

      newList.splice(destination.index, 0, target);

      const resources = updateItemPositonOnList(newList);

      dispatch(
        coreActions.updateResources({
          resourceType: 'programWeeks',
          resources,
        }),
      );

      dispatch(
        coreActions.requestUpdateItems({
          resources,
          resourceType: 'programWeeks',
        }),
      );
    }
  };

  const handleUpdateProgram = (payload) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: programId,
        payload,
        resourceType,
      }),
    );
  };

  const handleUpdateWeek = (payload, weekId) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: weekId,
        payload,
        resourceType: 'programWeeks',
      }),
    );
  };

  const handleAddWorkout = (option, weekId) => {
    const position = programWorkouts.filter(w => w.week_id === weekId).length + 1;
    const onSuccess = (wk) => {
      dispatch(
        uiActions.setUiState({
          selectedProgramWorkouts: {
            _merge: true,
            [wk.program_id]: {
              id: wk.id,
              weekId: wk.week_id,
            },
          },
        }),
      );
    };

    const data = (option.__isNew__ || option.newOne)
      ? {
        name: option.__isNew__ ? option.value : null,
      }
      : {
        [option.isPersonalWorkout ? 'personal_workout_id' : 'workout_template_id']: +option.value,
      };

    dispatch(
      coreActions.requestAddProgramWorkout({
        payload: {
          position,
          program_id: programId,
          program_type: programType,
          week_id: weekId,
          ...data,
        },
        callbacks: {
          onSuccess,
        },
      }),
    );
  };

  const handleCopyWeek = (week) => {
    const weeksCount = weeks.filter(w => w.id !== -1).length;
    dispatch(
      coreActions.requestCopyProgramWeek({
        week,
        programId,
        programType,
      }),
    );
    handleUpdateProgram({
      weeks_count: weeksCount + 1,
    });
  };

  const handleRemoveWeek = (week) => {
    const weeksCount = weeks.filter(w => w.id !== -1).length;

    const onSuccess = () => {
      handleUpdateProgram({
        weeks_count: weeksCount - 1,
      });

      if (week.workouts.some(w => w.id === selectedWorkout.id)) {
        makeFirstSelect();
      }

      if (week.workouts.length) {
        dispatch(
          coreActions.deleteResources({
            resourceType: 'programWorkouts',
            resources: week.workouts,
          }),
        );
      }
    };

    const onConfirmAction = coreActions.requestDeleteEntity({
      id: week.id,
      resourceType: 'programWeeks',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete Program Week',
          bodyText: `Are you sure you want to delete <b>${week.name}</b>?`,
          confirmText: 'Delete',
        },
        onSuccess,
        onConfirmAction,
      }),
    );
  };

  return readOnly ? (
    <S.Weeks>
      {weeks.map(week => (
        <Week
          key={week.id}
          week={week}
          readOnly
          selectedWorkoutId={selectedWorkout?.id}
          onSelectWorkout={handleSelectWorkout}
        />
      ))}
    </S.Weeks>
  ) : (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable
        type="week"
        droppableId="weeks"
      >
        {droppableProvided => (
          <S.Weeks
            ref={droppableProvided.innerRef}
            width="100%"
            column
          >
            {weeks.map((week, index) => (
              <Week
                key={week.id}
                week={week}
                index={index}
                resourceType={resourceType}
                workoutOptions={workoutOptions}
                onCopy={() => handleCopyWeek(week)}
                onUpdate={payload => handleUpdateWeek(payload, week.id)}
                onRemove={() => handleRemoveWeek(week)}
                onAddWorkout={(option) => handleAddWorkout(option, week.id)}
                selectedWorkoutId={selectedWorkout?.id}
                onSelectWorkout={handleSelectWorkout}
              />
            ))}
            {droppableProvided.placeholder}
          </S.Weeks>
        )}
      </Droppable>

    </DragDropContext>
  );
};

export default ProgramWeeks;
